import { useEffect, useRef, useState } from "react";
import { CargasTancadaApi } from "../api/CargasTancadaApi";
import { PESO_KG_PACA_PRODUCCION } from "../../../../../../../../../utility/constants";
import { formatPalletLabel } from "../../../../../helpers/formatPallet";
import { IsAlmacenReproceso } from "../helpers/almacenReproceso";

export const MODO_PACAS = "pacas";
export const MODO_SOBRANTE = "sobrante";

// El reproceso de una carga se toma del inventario del almacén: pacas de uno o
// varios pallets, o kg de sobrante de uno o varios productos, nunca las dos
// cosas en la misma carga. Los kg de la carga salen de lo tomado, no de la
// báscula. La carga se agrega desde el mismo modal, así que la selección solo
// vive mientras el modal está abierto.
export const useReprocesoCarga = (almacen) => {
    const {
        getInventarioReproceso,
        getSobranteReproceso,
        getPalletsLlegadosDespues,
        cargando,
    } = CargasTancadaApi();

    // solo los almacenes de reproceso tienen inventario que consultar
    const almacenId =
        almacen && IsAlmacenReproceso({ almacen }) ? almacen.id : null;

    const [modo, setModo] = useState(MODO_PACAS);

    const [inventarios, setInventarios] = useState([]);
    const [sobrantes, setSobrantes] = useState([]);
    const [palletsOcultos, setPalletsOcultos] = useState([]);

    const [cantidades, setCantidades] = useState({});
    const [modalAbierto, setModalAbierto] = useState(false);

    // guard para descartar respuestas viejas cuando el almacén cambia rápido
    const peticionRef = useRef(0);

    const GetDisponibles = async () => {
        const peticion = ++peticionRef.current;

        // limpiar de inmediato lo del almacén anterior
        setInventarios([]);
        setSobrantes([]);
        setPalletsOcultos([]);

        if (!almacenId) return;

        const params = { almacen_id: almacenId };

        const [inventariosReproceso, sobrantesReproceso, pallets] =
            await Promise.all([
                getInventarioReproceso(params),
                getSobranteReproceso(params),
                getPalletsLlegadosDespues(params),
            ]);

        if (peticion !== peticionRef.current) return;

        setInventarios(inventariosReproceso);
        setSobrantes(sobrantesReproceso);
        setPalletsOcultos(pallets);
    };

    const LimpiarSeleccion = () => {
        setCantidades({});
    };

    useEffect(() => {
        LimpiarSeleccion();
        GetDisponibles();
    }, [almacenId]);

    // cada apertura del modal arma una selección nueva: al agregar la carga lo
    // seleccionado ya se consumió, y al regresar se descarta
    const AbrirModal = () => {
        LimpiarSeleccion();
        setModalAbierto(true);
    };

    const CerrarModal = () => {
        LimpiarSeleccion();
        setModalAbierto(false);
    };

    const CambiarCantidad = (origenId, cantidad) => {
        setCantidades((prev) => ({ ...prev, [origenId]: cantidad }));
    };

    const esModoPacas = modo === MODO_PACAS;

    // pacas y sobrante se muestran y se editan igual, así que se normalizan a
    // una sola forma para reutilizar la tabla del modal
    const origenes = esModoPacas
        ? inventarios.map((inventario) => ({
              id: inventario.id,
              etiqueta: formatPalletLabel(
                  inventario.pallet.numero,
                  inventario.pallet.identificador_fisico,
              ),
              detalle: inventario.pallet.producto_nombre,
              disponible: Number(inventario.pacas_disponibles || 0),
          }))
        : sobrantes.map((existencia) => ({
              id: existencia.id,
              etiqueta: existencia.producto.nombre,
              detalle: existencia.producto.padre_producto_terminado_nombre,
              disponible: Number(existencia.kg_disponibles || 0),
          }));

    // total disponible en el almacén, sin importar el modo ni lo seleccionado:
    // las pacas se convierten a kg para poder sumarlas con el sobrante
    const calcularDisponibleAlmacen = () => {
        const pacas = inventarios.reduce(
            (suma, inventario) =>
                suma + Number(inventario.pacas_disponibles || 0),
            0,
        );

        const kgSobrante = sobrantes.reduce(
            (suma, existencia) => suma + Number(existencia.kg_disponibles || 0),
            0,
        );

        const kgPacas = pacas * PESO_KG_PACA_PRODUCCION;

        return {
            pacas,
            kgPacas,
            kgSobrante,
            kgTotal: kgPacas + kgSobrante,
        };
    };

    // se filtra sobre los orígenes vigentes para que una cantidad de un almacén
    // anterior nunca cuente
    const seleccionados = origenes.filter(
        (origen) => Number(cantidades[origen.id] || 0) > 0,
    );

    const calcularTotales = () => {
        const total = seleccionados.reduce(
            (suma, origen) => suma + Number(cantidades[origen.id] || 0),
            0,
        );

        return {
            pacas: esModoPacas ? total : null,
            kg: esModoPacas ? total * PESO_KG_PACA_PRODUCCION : total,
        };
    };

    const ObtenerConsumos = () => {
        if (esModoPacas) {
            return {
                consumos_pallet: seleccionados.map((origen) => ({
                    inventario_reproceso_id: origen.id,
                    pacas: Number(cantidades[origen.id]),
                })),
            };
        }

        return {
            consumos_sobrante: seleccionados.map((origen) => ({
                existencia_id: origen.id,
                kg_secos: Number(cantidades[origen.id]),
            })),
        };
    };

    return {
        modo,
        setModo,
        esModoPacas,

        origenes,
        palletsOcultos,
        nombreAlmacen: almacen ? almacen.nombre : "",
        hayDisponibles: origenes.length > 0,
        disponibleAlmacen: calcularDisponibleAlmacen(),

        cantidades,
        haySeleccion: seleccionados.length > 0,
        totales: calcularTotales(),

        modalAbierto,
        AbrirModal,
        CerrarModal,
        CambiarCantidad,
        LimpiarSeleccion,
        Refrescar: GetDisponibles,

        ObtenerConsumos,
        cargando,
    };
};
